import { ReactNode, FormEvent, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from '@/components/ui/button';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { overlayCardClass, overlayCardSizes, overlayScrimModalClass } from '@/lib/overlayStyles';

export interface ModalProps {
  submitText: string;
  submitButton: 'default' | 'destructive';
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onClose?: () => void;
  children: ReactNode;
  /** Text for the dismiss button (defaults to "Cancel"). */
  cancelText?: string;
  /** Disable the submit button (e.g. until a required checkbox is ticked). */
  submitDisabled?: boolean;
}

export default function FormModal({
  submitText,
  submitButton,
  onSubmit,
  onClose,
  children,
  cancelText = 'Cancel',
  submitDisabled = false,
}: ModalProps) {
  useEffect(() => {
    document.body.classList.add('no-scroll');
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, []);

  const handleClose = () => {
    onClose?.();
  };

  const stopPropagation = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return createPortal(
    <div className={cn(overlayScrimModalClass)} onClick={handleClose} role="presentation">
      <div
        className={cn(overlayCardClass, overlayCardSizes.lg)}
        onClick={stopPropagation}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-end px-5 pt-5">
          <IconButton onClick={handleClose} variant="ghost-light" size="icon" className="rounded-lg" icon={X} />
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-5 px-5 pb-6 pt-1">
          <div className="surface-tile flex flex-col gap-5 p-5">{children}</div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <IconButton type="button" size="default" variant="ghost-light" className="rounded-lg px-5" onClick={handleClose}>
              {cancelText}
            </IconButton>
            <IconButton
              type="submit"
              size="default"
              variant={submitButton}
              className="rounded-lg px-6"
              disabled={submitDisabled}
            >
              {submitText}
            </IconButton>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
